import React from "react";
import { Link, useLocation } from "react-router-dom";
import { Mail, Phone, MapPin } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";

export default function Footer() {
  const { t, prefix } = useLang();
  const location = useLocation();
  const isHome =
    location.pathname === "/ec" ||
    location.pathname === "/en" ||
    location.pathname === "/ec/" ||
    location.pathname === "/en/";
  const anchorHref = (hash) => (isHome ? hash : `${prefix}${hash}`);

  return (
    <footer
      data-testid="site-footer"
      className="bg-slate-950 text-slate-300 border-t border-slate-800"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-14">
        <div className="grid grid-cols-1 gap-10 md:grid-cols-4">
          <div className="md:col-span-2">
            <Link
              to={prefix}
              data-testid="footer-logo"
              className="inline-flex items-center gap-2 font-heading text-xl font-bold text-white"
            >
              <span className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-teal-500 text-slate-950 font-extrabold">
                C
              </span>
              <span>{t("nav.brand")}</span>
            </Link>
            <p className="mt-4 max-w-md text-sm leading-relaxed text-slate-400">
              {t("footer.tagline")}
            </p>
            <ul className="mt-6 space-y-2 text-sm text-slate-400">
              <li className="flex items-start gap-2">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-teal-500" />
                <span>{t("footer.legal.address")}</span>
              </li>
              <li className="flex items-center gap-2">
                <Phone className="h-4 w-4 shrink-0 text-teal-500" />
                <a
                  href={`tel:${t("footer.legal.phone").replace(/\s/g, "")}`}
                  data-testid="footer-phone"
                  className="hover:text-white"
                >
                  {t("footer.legal.phone")}
                </a>
              </li>
              <li className="flex items-center gap-2">
                <Mail className="h-4 w-4 shrink-0 text-teal-500" />
                <a
                  href={`mailto:${t("footer.legal.email")}`}
                  data-testid="footer-email"
                  className="hover:text-white"
                >
                  {t("footer.legal.email")}
                </a>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wider text-white">
              {t("footer.companyTitle")}
            </h3>
            <ul className="mt-4 space-y-2 text-sm">
              <li>
                <Link
                  to={prefix}
                  data-testid="footer-link-home"
                  className="hover:text-white"
                >
                  {t("footer.links.home")}
                </Link>
              </li>
              <li>
                <a
                  href={anchorHref("#service")}
                  data-testid="footer-link-service"
                  className="hover:text-white"
                >
                  {t("footer.links.service")}
                </a>
              </li>
              <li>
                <a
                  href={anchorHref("#how")}
                  data-testid="footer-link-how"
                  className="hover:text-white"
                >
                  {t("footer.links.how")}
                </a>
              </li>
              <li>
                <a
                  href={anchorHref("#advantages")}
                  data-testid="footer-link-advantages"
                  className="hover:text-white"
                >
                  {t("footer.links.advantages")}
                </a>
              </li>
              <li>
                <a
                  href={anchorHref("#faq")}
                  data-testid="footer-link-faq"
                  className="hover:text-white"
                >
                  {t("footer.links.faq")}
                </a>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wider text-white">
              {t("footer.legalTitle")}
            </h3>
            <ul className="mt-4 space-y-2 text-sm">
              <li>
                <Link
                  to={`${prefix}/privacy`}
                  data-testid="footer-link-privacy"
                  className="hover:text-white"
                >
                  {t("footer.links.privacy")}
                </Link>
              </li>
              <li>
                <Link
                  to={`${prefix}/cookies`}
                  data-testid="footer-link-cookies"
                  className="hover:text-white"
                >
                  {t("footer.links.cookies")}
                </Link>
              </li>
              <li>
                <Link
                  to={`${prefix}/terms`}
                  data-testid="footer-link-terms"
                  className="hover:text-white"
                >
                  {t("footer.links.terms")}
                </Link>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-12 border-t border-slate-800 pt-8 grid grid-cols-1 gap-3 md:grid-cols-2 text-xs text-slate-500">
          <div>
            <p className="font-semibold text-slate-300">
              {t("footer.legal.company")}
            </p>
            <p>{t("footer.legal.ruc")}</p>
            <p>{t("footer.legal.supercias")}</p>
            <p>{t("footer.legal.license")}</p>
          </div>
          <div className="md:text-right">
            <p>{t("footer.legal.jurisdiction")}</p>
            <p>{t("footer.legal.incorporation")}</p>
            <p className="mt-2">{t("footer.legal.rights")}</p>
          </div>
        </div>
        <p className="mt-4 text-xs text-slate-500">
          {t("footer.legal.disclaimer")}
        </p>
      </div>
    </footer>
  );
}
